/** The app's route registry for the Nucleus assistant's direct_navigate tool.
 *
 * This is the SOURCE OF TRUTH for assistant navigation: the dock sends it with
 * every turn (uiContext.route_registry), and core builds the tool's routing
 * text and validates picked paths against it — so a new page here is
 * immediately navigable by the assistant, no core change. Core keeps a
 * fallback snapshot (features/nucleus/tools/navigation.py) for surfaces that
 * don't send one; keep the two aligned when editing.
 *
 * Keep it aligned with the router in routes.tsx (paths must exist there), and
 * write `description`/`aliases` for ROUTING: what a user would SAY, not what
 * the page is called. `tabs` maps a URL hash to an in-page tab
 * (/data#connections); a page's default tab is the bare path and isn't listed.
 */

export interface RouteRegistryEntry {
  path: string
  label: string
  aliases: string[]
  description: string
  tabs?: Record<string, string>
}

export const ROUTE_REGISTRY: RouteRegistryEntry[] = [
  {
    path: "/data",
    label: "Data",
    aliases: ["master data", "data page", "data hub", "sources"],
    description:
      "Master-data landing page: mapped data sets and their status, refresh runs, "
      + "source connections, and data tickets.",
    tabs: {
      refreshes: "Refresh runs — master-data pipeline refresh history and status.",
      connections:
        "Data source connections — where raw feeds land and get mapped "
        + "(what the retired Data Hub page covered).",
      tickets: "Data-related Linear tickets.",
    },
  },
  {
    path: "/digital-twin",
    label: "Digital Twin",
    aliases: ["twin", "network"],
    description: "The supply-chain network model: nodes, edges, modules.",
  },
  {
    path: "/demand",
    label: "Demand",
    aliases: ["demand reporting", "forecast"],
    description: "Demand reporting and sales-forecast views.",
  },
  {
    path: "/replenishment",
    label: "Replenishment",
    aliases: ["replenishment module"],
    description: "The replenishment planning module.",
  },
  {
    path: "/reporting",
    label: "Reporting",
    aliases: ["reports", "queryviz"],
    description:
      "The report library (QueryViz reports: unified orders, inventory, time series...).",
  },
  {
    path: "/balance-walk",
    label: "Balance Walk",
    aliases: ["walk", "supply walk"],
    description: "Balance-walk engine runs: inventory walk by node/item/week.",
  },
  {
    path: "/allocation-engine",
    label: "Allocation Engine",
    aliases: ["allocation", "engine runs"],
    description: "Allocation engine runs, inputs, and stocking targets.",
  },
  {
    path: "/trackers",
    label: "Tracker",
    aliases: ["plan tracker", "approvals"],
    description: "Plan tracker: uploaded plan versions, comparisons, approvals.",
  },
  {
    path: "/runs/history",
    label: "Run History",
    aliases: ["runs", "run history"],
    description: "History of the Replenishment module's engine runs.",
  },
  {
    path: "/tickets",
    label: "Tickets",
    aliases: ["linear", "issues"],
    description: "Linear tickets for this workspace.",
  },
  {
    path: "/docs",
    label: "Docs",
    aliases: ["documentation", "help"],
    description: "Product documentation.",
  },
  {
    path: "/user-admin",
    label: "User Admin",
    aliases: ["users", "admin"],
    description: "User management: invites, roles.",
  },
]
